import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { Page, Reveal, MaskedLine, EASE } from "@/components/Page";
import { useLang } from "@/i18n";
import { useContent } from "@/content";
import { useSeo } from "@/hooks/useSeo";

const localized = (value, lang, fallback = "") => {
  if (value == null) return fallback;

  if (typeof value === "string" || typeof value === "number") {
    return value;
  }

  if (typeof value === "object") {
    return value[lang] ?? value.tr ?? value.en ?? fallback;
  }

  return fallback;
};

const ServiceRow = ({ service, index, open, onToggle }) => (
  <div className="border-t border-ink/10 last:border-b">
    <button
      type="button"
      data-testid={`service-toggle-${index}`}
      onClick={onToggle}
      aria-expanded={open}
      className="group w-full grid grid-cols-12 items-center gap-4 py-7 sm:py-10 text-left"
    >
      <span className="col-span-2 sm:col-span-1 font-mono text-xs text-clay">
        {String(index + 1).padStart(2, "0")}
      </span>

      <span
        className={`col-span-9 sm:col-span-10 text-xl sm:text-3xl lg:text-4xl font-light uppercase tracking-tight transition-colors duration-500 ${
          open ? "text-solidor" : "group-hover:text-solidor"
        }`}
      >
        {service.title}
      </span>

      <motion.span
        className="col-span-1 justify-self-end"
        animate={{ rotate: open ? 45 : 0 }}
        transition={{ duration: 0.4, ease: EASE }}
      >
        <Plus
          className="h-5 w-5 sm:h-6 sm:w-6"
          strokeWidth={1}
        />
      </motion.span>
    </button>

    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.5, ease: EASE }}
          className="overflow-hidden"
        >
          <p
            data-testid={`service-description-${index}`}
            className="col-span-12 max-w-2xl pb-10 sm:pb-12 pl-[18%] sm:pl-[9%] text-sm sm:text-base font-light leading-relaxed text-ink/70"
          >
            {service.text}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export default function Services() {
  const [open, setOpen] = useState(0);
  const { lang, t } = useLang();
  const cms = useContent();

  const seo = cms.site?.seo?.services || {};

  useSeo({
    title: localized(seo.title, lang, ""),
    description: localized(seo.description, lang, ""),
  });

  const services = Array.isArray(cms.services)
    ? cms.services
    : [];

  const items = services.map((s, i) => ({
    id: s?.id || `service-${i}`,
    title: localized(s?.title, lang, ""),
    text: localized(s?.text, lang, ""),
  }));

  return (
    <Page
      testid="services-page"
      className="bg-bone min-h-screen"
    >
      <section className="px-5 sm:px-10 pt-36 sm:pt-48 pb-24 sm:pb-36">
        <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-clay">
          <MaskedLine delay={0.2}>
            {t.services.label}
          </MaskedLine>
        </span>

        <h1 className="mt-6 text-2xl sm:text-4xl lg:text-5xl font-light uppercase tracking-tight leading-[0.95]">
          <MaskedLine delay={0.35}>
            {t.services.title}
          </MaskedLine>
        </h1>

        <div className="mt-16 sm:mt-24">
          <Reveal>
            {items.map((s, i) => (
              <ServiceRow
                key={s.id}
                service={s}
                index={i}
                open={open === i}
                onToggle={() =>
                  setOpen(open === i ? -1 : i)
                }
              />
            ))}
          </Reveal>
        </div>
      </section>
    </Page>
  );
}
